import { parseISO, eachDayOfInterval, format, startOfMonth, endOfMonth, getDay } from 'date-fns';
import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';

export default function HolidayChart() {
  const [holidayData, setHolidayData] = useState([]);
  const [allHolidays, setAllHolidays] = useState([]); // Store all holidays for filtering
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [daysOpen, setDaysOpen] = useState("");
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [maxOnLeaveMap, setMaxOnLeaveMap] = useState({});
  
  // Filter states
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedAccess, setSelectedAccess] = useState("staff"); // Default to staff
  const [selectedAccessDepartment, setSelectedAccessDepartment] = useState(""); // For manager/admin department filter
  
  const user = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch holidays
        const holidayResponse = await fetch('https://api.insightos.co.uk/api/holiday', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        // Fetch departments
        const departmentResponse = await fetch('https://api.insightos.co.uk/api/getdepartments', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        // Fetch policies
        const policyResponse = await fetch('https://api.insightos.co.uk/api/policies', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        const holidayData = await holidayResponse.json();
        const departmentData = await departmentResponse.json();
        const policyData = await policyResponse.json();

        const approvedHolidays = holidayData.data.filter(holiday => holiday.Approved === 1);

        const departments = departmentData.data.map(dept => dept.Name);
        const maxOnLeaveMap = {};
        departmentData.data.forEach(dept => {
          maxOnLeaveMap[dept.Name] = dept.MaxOnLeave;
        })
        

        const roles = [...new Set(approvedHolidays.map(holiday => holiday.Role))];
        const daysOpen = policyData.data[0].DaysOpen;

        if (!daysOpen || typeof daysOpen !== 'string') {
          console.error('Invalid DaysOpen string:', daysOpen);
          return;
        }

        setAllHolidays(approvedHolidays);
        setDepartments(departments);
        setRoles(roles);
        setDaysOpen(daysOpen);
        setMaxOnLeaveMap(maxOnLeaveMap);

        // Apply initial filter
        applyFilters(approvedHolidays, daysOpen, "staff", "", "", maxOnLeaveMap);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const applyFilters = (holidays, daysOpenStr, access, department, role, maxOnLeaveMap) => {
    let filteredHolidays = holidays;

    // Filter by access level
    if (access === "staff") {
      // Include only staff (exclude managers and admins)
      filteredHolidays = filteredHolidays.filter(holiday => 
        holiday.Access === "staff" || !holiday.Access || holiday.Access === ""
      );
    } else if (access === "manager" || access === "admin") {
      // Include only selected access level
      filteredHolidays = filteredHolidays.filter(holiday => holiday.Access === access);
    }

    // Filter by department
    if (department) {
      filteredHolidays = filteredHolidays.filter(holiday => holiday.Department === department);
    }

    // Filter by role
    if (role) {
      filteredHolidays = filteredHolidays.filter(holiday => holiday.Role === role);
    }

    const holidaysByMonth = calculateHolidaysByMonth(filteredHolidays, daysOpenStr, maxOnLeaveMap, department);
    setHolidayData(holidaysByMonth);
  };

  const handleFilterChange = () => {
    const department = selectedAccess === "staff" ? selectedDepartment : selectedAccessDepartment;
    applyFilters(allHolidays, daysOpen, selectedAccess, department, selectedRole, maxOnLeaveMap);
  };

  useEffect(() => {
    if (allHolidays.length > 0 && daysOpen) {
      handleFilterChange();
    }
  }, [selectedDepartment, selectedRole, selectedAccess, selectedAccessDepartment, maxOnLeaveMap]);

  const calculateHolidaysByMonth = (holidayRequests, daysOpen, maxOnLeaveMap, selectedDepartment) => {
    const holidaysByMonth = [];

    for (let i = 0; i < 12; i++) {
      holidaysByMonth[i] = { 
        month: i, 
        taken: 0, 
        totalAvailableDays: 0, 
        percentageTaken: 0, 
        availableDays: [], 
        takenDays: [], // Will now store objects with date and count
        maxOnLeave: selectedDepartment ? maxOnLeaveMap[selectedDepartment] : null
      };
    }

    holidayRequests.forEach((holiday) => {
      const fromDate = parseISO(holiday.FromDate);
      const toDate = parseISO(holiday.ToDate);
      const month = fromDate.getMonth();

      const daysBetween = eachDayOfInterval({ start: fromDate, end: toDate });
      const dayNames = daysBetween.map((day) => format(day, 'EEEE'));
      const matchedDays = daysBetween.filter((day, idx) => daysOpen.includes(dayNames[idx]));

      holidaysByMonth[month].taken += matchedDays.length;
      
      // Track people per day
      matchedDays.forEach(day => {
        const dayNum = day.getDate();
        const existingEntry = holidaysByMonth[month].takenDays.find(entry => entry.day === dayNum);
        if (existingEntry) {
          existingEntry.count += 1;
        } else {
          holidaysByMonth[month].takenDays.push({ day: dayNum, count: 1 });
        }
      });
    });

    holidaysByMonth.forEach((monthData, index) => {
      const startOfMonthDate = new Date(2025, index, 1);
      const endOfMonthDate = new Date(2025, index + 1, 0);

      const daysInMonth = eachDayOfInterval({ start: startOfMonthDate, end: endOfMonthDate });

      const workingDaysInMonth = daysInMonth.filter((day) => {
        const dayName = format(day, 'EEEE');
        return daysOpen.includes(dayName);
      });

      monthData.totalAvailableDays = workingDaysInMonth.length;
      monthData.availableDays = workingDaysInMonth.map((d) => d.getDate());

      if (monthData.totalAvailableDays > 0) {
        monthData.percentageTaken = (monthData.taken / monthData.totalAvailableDays) * 100;
      }
    });

    return holidaysByMonth;
  };

  const renderCalendar = (monthData) => {
    const firstDayOfMonth = startOfMonth(new Date(2025, monthData.month, 1));
    const lastDayOfMonth = endOfMonth(firstDayOfMonth);
    const daysInMonth = eachDayOfInterval({ start: firstDayOfMonth, end: lastDayOfMonth });

    const firstWeekday = (getDay(firstDayOfMonth) + 6) % 7;
    const emptyDaysBefore = Array(firstWeekday).fill(null);

    return (
      <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
        <button 
          onClick={() => setSelectedMonthData(null)} 
          className="inline-flex items-center px-3 py-2 text-sm font-semibold text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200"
        >
          ← Back to Overview
        </button>
        <h4 className="mt-4 mb-6 text-2xl font-bold text-gray-900">
          {format(firstDayOfMonth, 'MMMM 2025')}
        </h4>
        
        <div className="grid grid-cols-7 text-center text-sm font-semibold text-gray-700 mb-2">
          <div className="py-2">Mon</div>
          <div className="py-2">Tue</div>
          <div className="py-2">Wed</div>
          <div className="py-2">Thu</div>
          <div className="py-2">Fri</div>
          <div className="py-2">Sat</div>
          <div className="py-2">Sun</div>
        </div>
        
        <div className="grid grid-cols-7 gap-1 rounded-lg overflow-hidden">
          {emptyDaysBefore.map((_, index) => (
            <div key={`empty-${index}`} className="h-12 bg-gray-50"></div>
          ))}

          {daysInMonth.map((day) => {
            const dayNum = day.getDate();
            const isAvailable = monthData.availableDays.includes(dayNum);
            const takenEntry = monthData.takenDays.find(entry => entry.day === dayNum);
            const peopleOnLeave = takenEntry ? takenEntry.count : 0;

            let bgColor = 'bg-gray-100 text-gray-400';
            let label = 'Closed';
            
            if (isAvailable) {
              if (peopleOnLeave > 0) {
                if (monthData.maxOnLeave && peopleOnLeave >= monthData.maxOnLeave) {
                  bgColor = 'bg-red-600 text-white hover:bg-red-700';
                  label = `Over Limit: ${peopleOnLeave} on leave (Max: ${monthData.maxOnLeave})`;
                } else if (monthData.maxOnLeave && peopleOnLeave === monthData.maxOnLeave - 1) {
                  bgColor = 'bg-orange-500 text-white hover:bg-orange-600';
                  label = `Near Limit: ${peopleOnLeave} on leave (Max: ${monthData.maxOnLeave})`;
                } else {
                  bgColor = 'bg-red-500 text-white hover:bg-red-600';
                  label = `${peopleOnLeave} on leave${monthData.maxOnLeave ? ` (Max: ${monthData.maxOnLeave})` : ''}`;
                }
              } else {
                bgColor = 'bg-green-500 text-white hover:bg-green-600';
                label = 'Available';
              }
            }

            return (
              <div
                key={dayNum}
                className={`h-12 flex items-center justify-center text-sm font-medium transition-colors duration-200 ${bgColor} relative`}
                title={label}
              >
                <span className="relative z-10">{dayNum}</span>
                {peopleOnLeave > 0 && (
                  <div className="absolute bottom-1 right-1 bg-white bg-opacity-90 text-gray-800 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                    {peopleOnLeave}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 flex flex-wrap gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
            <span className="text-gray-700">Available Days</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
            <span className="text-gray-700">Holiday Taken</span>
          </div>
          {monthData.maxOnLeave && (
            <>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-orange-500 rounded mr-2"></div>
                <span className="text-gray-700">Near Limit</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-600 rounded mr-2"></div>
                <span className="text-gray-700">Over Limit</span>
              </div>
            </>
          )}
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gray-100 rounded mr-2"></div>
            <span className="text-gray-700">Closed Days</span>
          </div>
        </div>
        
        {monthData.maxOnLeave && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Max Staff on Leave:</strong> {monthData.maxOnLeave} people
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filter Controls */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Options</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Access Level Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Access Level
            </label>
            <select
              value={selectedAccess}
              onChange={(e) => setSelectedAccess(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="staff">Staff</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Department Filter for Staff */}
          {selectedAccess === "staff" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department (Staff)
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Department Filter for Manager/Admin */}
          {(selectedAccess === "manager" || selectedAccess === "admin") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department ({selectedAccess})
              </label>
              <select
                value={selectedAccessDepartment}
                onChange={(e) => setSelectedAccessDepartment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Role Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Holiday Data Display */}
      {!selectedMonthData && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {holidayData.map((monthData, index) => (
            <div key={index} className="bg-white p-6 border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200">
              <h4 className="text-lg font-bold text-blue-700 mb-4">
                {format(new Date(2025, monthData.month), 'MMMM')}
              </h4>
              
              <div className="mb-4">
                <div className="overflow-hidden rounded-full bg-gray-200 h-3">
                  <div
                    style={{ width: `${monthData.percentageTaken || 0}%` }}
                    className="h-3 rounded-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-300"
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {Math.round(monthData.percentageTaken || 0)}% utilized
                </p>
              </div>
              
              <button
                onClick={() => setSelectedMonthData(monthData)}
                className="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
              >
                View Calendar
              </button>
              
              <div className="mt-4 space-y-1 text-sm text-gray-600">
                <p className="flex justify-between">
                  <span>Available:</span>
                  <span className="font-semibold">{monthData.totalAvailableDays} days</span>
                </p>
                <p className="flex justify-between">
                  <span>Taken:</span>
                  <span className="font-semibold text-red-600">{monthData.taken} days</span>
                </p>
                {monthData.maxOnLeave && (
                  <p className="flex justify-between">
                    <span>Max on Leave:</span>
                    <span className="font-semibold text-blue-600">{monthData.maxOnLeave} people</span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedMonthData && renderCalendar(selectedMonthData)}
    </div>
  );
}